import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Mail, LogIn, Eye, EyeOff, Zap, BarChartHorizontalBig, BrainCircuit, Link as LinkIcon } from 'lucide-react'; // Added more relevant icons

// Sağ taraftaki animasyonlu görsel için bir component (Opsiyonel, basit CSS ile de yapılabilir)
// const MarketingVisual = () => (
//   <div className="absolute inset-0 overflow-hidden">
//     {/* Buraya animasyonlu SVG, Lottie, WebGL vb. eklenebilir */}
//     <div className="absolute w-96 h-96 bg-blue-500/30 rounded-full -top-10 -left-20 animate-pulse blur-3xl opacity-50"></div>
//     <div className="absolute w-72 h-72 bg-violet-500/30 rounded-full -bottom-10 -right-10 animate-pulse delay-1000 blur-3xl opacity-50"></div>
//     {/* Daha karmaşık CSS veya JS animasyonları eklenebilir */}
//   </div>
// );

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState({ loading: false, message: '', error: '' });
  const [showPassword, setShowPassword] = useState(false); // Şifre gösterme/gizleme
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  // Get redirect parameters from URL
  const redirectParam = searchParams.get('redirect');
  const shopParam = searchParams.get('shop');
  const sourceParam = searchParams.get('source');
  const chargeIdParam = searchParams.get('charge_id');
  
  // Shopify flow: redirect to /shopify-connect if coming from Shopify with charge_id
  useEffect(() => {
    if (shopParam && chargeIdParam) {
      // Redirect to shopify-connect page for proper flow
      console.log('[SHOPIFY] Redirecting to shopify-connect:', shopParam);
      window.location.href = `/shopify-connect?shop=${encodeURIComponent(shopParam)}&action=register&charge_id=${chargeIdParam}`;
    }
  }, [shopParam, chargeIdParam]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setResendStatus({ loading: false, message: '', error: '' });
    try {
      const response = await fetch('https://api.glowytics.com/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 403 && data.code === 'EMAIL_NOT_VERIFIED') {
          setNeedsVerification(true);
          throw new Error(data.error || 'Please verify your email before signing in.');
        }
        throw new Error(data.error || data.message || 'Login failed');
      }
      login(data.user);
      
      // Handle redirect after successful login
      if (redirectParam === 'select-brand' && shopParam) {
        // Redirect to select-brand with ikas pending store info
        navigate(`/select-brand?ikas_pending_store=${encodeURIComponent(shopParam)}&source=${sourceParam || 'ikas'}`);
      } else if (redirectParam) {
        // Generic redirect
        navigate(`/${redirectParam}`);
      } else {
        // Default redirect
        navigate('/');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'An unexpected error occurred.');
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50"> {/* Changed base background slightly */}

      {/* Left Column: Login Form & Controls */}
      <div className="w-full lg:w-[45%] xl:w-[40%] flex flex-col justify-center items-center p-8 sm:p-12 bg-white shadow-xl z-10"> {/* Added shadow */}
        <div className="w-full max-w-sm">
          {/* Logo */}
          <img
            src="/Calisma-Yuzeyi-20@5x-4.png" // Your logo path
            alt="Glowytics Logo"
            className="h-9 mb-10" // Slightly smaller logo
          />

          {/* Header */}
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Access Your Intelligence Hub
          </h1>
          <p className="text-sm text-gray-500 mb-8">
            Log in to manage, analyze, and automate your marketing.
          </p>

          {/* Error Message */}
          {error && (
            <div className="mb-6 rounded-md bg-red-50 p-3 border border-red-200">
              <p className="text-xs text-red-700 font-medium">{error}</p>
              {needsVerification && (
                <div className="mt-2">
                  {resendStatus.message && (
                    <p className="text-xs text-green-700 mb-2">{resendStatus.message}</p>
                  )}
                  {resendStatus.error && (
                    <p className="text-xs text-red-700 mb-2">{resendStatus.error}</p>
                  )}
                  <button
                    type="button"
                    onClick={async () => {
                      if (!email) return;
                      setResendStatus({ loading: true, message: '', error: '' });
                      try {
                        const r = await fetch('https://api.glowytics.com/api/auth/resend-verification', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ email }),
                        });
                        const d = await r.json().catch(() => ({}));
                        if (!r.ok) {
                          setResendStatus({ loading: false, message: '', error: d.error || 'Could not resend' });
                          return;
                        }
                        setResendStatus({ loading: false, message: 'Verification email sent. Check your inbox.', error: '' });
                      } catch (e) {
                        setResendStatus({ loading: false, message: '', error: e.message || 'Network error' });
                      }
                    }}
                    disabled={resendStatus.loading || !email}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-500 underline disabled:opacity-50"
                  >
                    {resendStatus.loading ? 'Sending…' : 'Resend verification email'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="email" name="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition sm:text-sm"
                  placeholder="you@company.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-medium text-gray-600 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="password" name="password" type={showPassword ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-9 pr-10 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition sm:text-sm"
                  placeholder="Enter password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500" aria-label={showPassword ? "Hide" : "Show"}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-1.5 text-right">
                <a href="/forgot-password" className="text-xs font-medium text-indigo-600 hover:text-indigo-500 transition">Forgot Password?</a>
              </div>
            </div>

            <button type="submit" className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition group">
              <LogIn className="h-4 w-4 text-indigo-300 group-hover:text-white transition" />
              Log In
            </button>
          </form>

          {/* Sign Up */}
          <p className="mt-8 text-center text-xs text-gray-500">
            Don't have an account?{' '}
            <a href="/signup" className="font-medium text-indigo-600 hover:text-indigo-500 transition">
              Create an Account
            </a>
          </p>

          {/* Footer */}
          <div className="mt-10 border-t border-gray-100 pt-5">
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-3 text-[11px] text-gray-400">
              <div className="flex items-center">
                <Lock className="h-3 w-3 mr-1 text-gray-400" />
                <span>Protected by</span>
                <img src="/Calisma-Yuzeyi-20@5x-4.png" alt="Glowytics" className="h-3.5 ml-1" />
              </div>
              <span className="hidden sm:inline text-gray-300">·</span> {/* Divider */}
              <div className="flex items-center">
                <Zap className="h-3 w-3 mr-1 text-gray-400" />
                <span>Powered by</span>
                <img src="/openai.png" alt="OpenAI" className="h-3.5 w-auto ml-1" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column: Visual Branding & Concept */}
      <div className="hidden lg:flex lg:w-[55%] xl:w-[60%] bg-gradient-to-br from-[#0D122B] via-[#101C48] to-[#1F2A68] relative items-center justify-center p-10 xl:p-16 text-white overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-0 opacity-40 mix-blend-soft-light">
           {/* Subtle grid */}
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
           {/* Larger, slower moving gradient shapes */}
          <div className="absolute w-[700px] h-[700px] bg-gradient-radial from-blue-600/30 via-transparent to-transparent rounded-full -top-60 -left-60 animate-pulse-slow filter blur-3xl"></div>
          <div className="absolute w-[600px] h-[600px] bg-gradient-radial from-violet-700/20 via-transparent to-transparent rounded-full -bottom-60 -right-60 animate-pulse-slow animation-delay-2000 filter blur-3xl"></div>
           {/* Fast moving small 'data' particles (example) */}
           {/* <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-cyan-300 rounded-full animate-ping"></div> */}
           {/* <div className="absolute bottom-1/3 right-1/3 w-1 h-1 bg-purple-300 rounded-full animate-ping delay-500"></div> */}
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 flex flex-col items-center justify-center text-center max-w-xl mx-auto">
           <img 
             src="/glowyticspreview.png" 
             alt="Glowytics Platform Showcase"
             className="object-contain max-h-[90vh] mb-10 [filter:drop-shadow(0_25px_25px_rgb(0_0_0/0.15))]"
           />
           <h2 className="text-3xl xl:text-4xl font-bold mb-4 leading-tight">
             Turn Data into Actionable Strategy
           </h2>
           <p className="text-base xl:text-lg text-indigo-100/70 mb-10">
             Glowytics centralizes your marketing data, providing AI-driven insights, planning, and automation for intelligent campaign growth.
           </p>
           <div className="flex justify-center space-x-8 text-indigo-200/70">
              <div className="flex flex-col items-center" title="Centralized Data">
                <LinkIcon className="h-6 w-6 mb-1" strokeWidth={1.5}/>
                <span className="text-xs font-light">Connect</span>
              </div>
              <div className="flex flex-col items-center" title="AI Analysis">
                <Zap className="h-6 w-6 mb-1" strokeWidth={1.5}/>
                <span className="text-xs font-light">Analyze</span>
              </div>
              <div className="flex flex-col items-center" title="Automated Actions">
                <BarChartHorizontalBig className="h-6 w-6 mb-1" strokeWidth={1.5}/>
                <span className="text-xs font-light">Automate</span>
              </div>
           </div>
         </div>
       </div>
     </div>
  );
}

// tailwind.config.js içinde eklenecek animasyonlar ve opsiyonel grid pattern:
/*
module.exports = {
  // ...
  theme: {
    extend: {
      animation: {
        'pulse-slow': 'pulse 5s cubic-bezier(0.4, 0, 0.6, 1) infinite', // Slower pulse
      },
      animationDelay: { // Eğer tailwindcss-animation-delay plugini kuruluysa
        '2000': '2000ms',
      },
      backgroundImage: {
        'gradient-radial': 'radial-gradient(var(--tw-gradient-stops))',
        'grid-pattern': 'linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)', // Subtle grid
      },
      backgroundSize: {
        'grid-size': '30px 30px', // Smaller grid size
      }
    },
  },
  plugins: [
    // require('tailwindcss-animation-delay'),
  ],
}
*/ 